import { Send, Facebook, Twitter, Linkedin, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { COMPANY_INFO } from '../constants/companyInfo';

const footerLinks = {
  product: {
    title: 'Ürün',
    links: [
      { name: 'AI Video Stüdyo', href: '/#modules' },
      { name: 'Gayrimenkul Araştırma', href: '/#modules' },
      { name: 'İlan Analizi', href: '/#features' },
      { name: 'Parsel ve Sanal Drone', href: '/#use-cases' },
      { name: 'Fiyatlandırma', href: '/#pricing' },
    ],
  },
  company: {
    title: 'Şirket',
    links: [
      { name: 'Hakkımızda', href: '/#how-it-works' },
      { name: 'Blog', href: '/blog' },
      { name: 'Kariyer', href: `mailto:${COMPANY_INFO.email}?subject=Kariyer` },
      { name: 'Basın', href: `mailto:${COMPANY_INFO.email}?subject=Basin` },
      { name: 'İletişim', href: '/#contact' },
    ],
  },
  resources: {
    title: 'Kaynaklar',
    links: [
      { name: 'Yardım Merkezi', href: '/#faq' },
      { name: 'API Dokümantasyonu', href: '/docs' },
      { name: 'Kılavuzlar', href: '/#how-it-works' },
      { name: 'Topluluk', href: 'https://www.linkedin.com/company/maplio' },
    ],
  },
  legal: {
    title: 'Yasal',
    links: [
      { name: 'Kullanım Koşulları', href: '/kullanim-kosullari' },
      { name: 'Gizlilik Politikası', href: '/gizlilik-politikasi' },
      { name: 'Çerez Politikası', href: '/cerez-politikasi' },
      { name: 'KVKK Aydınlatma Metni', href: '/kvkk-aydinlatma-metni' },
    ],
  },
};

const socialLinks = [
  { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/maplio' },
  { name: 'Twitter', icon: Twitter, href: 'https://x.com/maplio' },
  { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/maplio' },
  { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/maplio' },
];

function isExternalLink(href) {
  return href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
}

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400 py-16 border-t border-slate-800">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
          <div className="lg:col-span-2">
            <a href="/#hero" className="flex items-center gap-2 mb-6">
              <img src="/logo-white.png" alt="Maplio" className="h-8 w-auto object-contain" />
            </a>

            <p className="text-sm mb-6 max-w-xs leading-relaxed">
              Gayrimenkul tanıtımında yapay zeka devrimi. Saniyeler içinde uydu destekli profesyonel videolar oluşturun.
            </p>

            <div className="grid gap-3 text-sm mb-6">
              <a href={`mailto:${COMPANY_INFO.email}`} className="inline-flex items-start gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4 mt-0.5" />
                <span>{COMPANY_INFO.email}</span>
              </a>
              <a href={`tel:${COMPANY_INFO.phoneTel}`} className="inline-flex items-start gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4 mt-0.5" />
                <span>{COMPANY_INFO.phoneDisplay}</span>
              </a>
              <p className="inline-flex items-start gap-2 text-slate-300">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{COMPANY_INFO.address}</span>
              </p>
            </div>

            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h4 className="text-white font-bold mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      target={isExternalLink(link.href) ? '_blank' : undefined}
                      rel={isExternalLink(link.href) ? 'noopener noreferrer' : undefined}
                      className="text-sm hover:text-white transition-colors"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-800 pt-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-white font-bold mb-1">Bültenimize Abone Olun</h4>
              <p className="text-sm">Yeni özellikler ve güncellemelerden haberdar olun.</p>
            </div>
            <form className="flex gap-2 w-full md:w-auto" onSubmit={(event) => event.preventDefault()}>
              <input
                type="email"
                placeholder="E-posta adresiniz"
                className="flex-1 md:w-64 px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
              />
              <button
                type="submit"
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors"
                aria-label="Abone ol"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-center md:text-left">© {new Date().getFullYear()} Maplio. Tüm hakları saklıdır.</p>
          <div className="flex items-center gap-6 text-xs">
            <a href="/kvkk-aydinlatma-metni" className="hover:text-white transition-colors">
              KVKK Aydınlatma Metni
            </a>
            <a href="/cerez-politikasi#cerez-yonetimi" className="hover:text-white transition-colors">
              Çerez Ayarları
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
